import Link from 'next/link';
import { ARTICLE_TOKENS } from '../design-tokens';

import { TeamBadge } from '@components/transfers/TeamBadge';
import {
  buildPlayerSlug,
  toPlayerProfilePath,
  toSlug
} from '@lib/page-utils/player-profile/slugs';
import type {
  ArticleBlockData,
  SanityPlayerListBlock
} from '@lib/article-blocks/types';

type PlayerListBlockProps = {
  value: SanityPlayerListBlock;
  data: ArticleBlockData;
};

/**
 * Chips link to the canonical profile URL built from club and web name. The
 * collision suffix (two squad mates sharing a web name) needs the whole squad,
 * which this page does not have; the profile route resolves the bare slug to
 * the first match, so the rare collision degrades to a redirect, not a 404.
 */
export const PlayerListBlock = ({ value, data }: PlayerListBlockProps) => {
  const players = (value.players ?? [])
    .map((reference) => data.playersByOptaUuid[reference.optaUuid])
    .filter((player): player is NonNullable<typeof player> => Boolean(player));
  if (players.length === 0) return null;

  return (
    <section className="not-prose my-8" data-cy="player-list">
      <header className="mb-4 flex items-center gap-3">
        <h3
          className={`m-0 font-heading text-[15px] font-extrabold tracking-[-0.01em] ${ARTICLE_TOKENS.ink}`}
        >
          {value.title}
        </h3>
        <span
          aria-hidden
          className={`h-px flex-1 border-t ${ARTICLE_TOKENS.rule}`}
        />
        <span
          className={`font-body tabular-nums text-[11.5px] ${ARTICLE_TOKENS.mutedLight}`}
        >
          {players.length} {players.length === 1 ? 'player' : 'players'}
        </span>
      </header>
      <div className="flex flex-wrap gap-2">
        {players.map((player) => (
          <Link
            key={player.externalIds.optaUuid}
            href={toPlayerProfilePath(
              toSlug(player.team.fullName),
              buildPlayerSlug(player, [])
            )}
            className={`flex items-center gap-2 rounded-full border bg-white py-[7px] pl-2 pr-3 text-sm leading-none !no-underline transition-all duration-150 ease-out hover:-translate-y-0.5 hover:border-[#3fa9f5] hover:bg-[#f4faff] hover:!no-underline ${ARTICLE_TOKENS.rule}`}
          >
            <TeamBadge
              shortName={player.team.shortName}
              size="xs"
              className="!m-0 !inline-block"
            />
            <span className={`font-semibold ${ARTICLE_TOKENS.ink}`}>
              {player.displayName}
            </span>
            <span
              className={`font-body text-[11.5px] tabular-nums ${ARTICLE_TOKENS.mutedLight}`}
            >
              £{player.price.toFixed(1)}m
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
};
